import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { HeaderText, PageTitle } from '@constants/global.enum';
import { associationOptions, validFileTypes } from './onboard-vendor.config';
import {
  FormControl,
  FormGroup,
  Validators,
  FormArray,
  FormBuilder,
} from '@angular/forms';
import { getFormControlError } from './onboard-vendor.helper';
import { ToasterService } from '@services/toaster.service';
import { uploadedFileValidator } from '@utils/validators';
import { FileUploadService } from '@services/file-upload.service';
import { ButtonLabels, ErrorMessages, FieldLabels } from '@constants/constants';

@Component({
  selector: 'app-onboard-vendor',
  templateUrl: './onboard-vendor.component.html',
  styleUrl: './onboard-vendor.component.scss',
})
export class OnboardVendorComponent {
  pageTitleEnum = PageTitle;
  headerTextEnum = HeaderText;
  fieldLabels = FieldLabels;
  errorMessages = ErrorMessages;
  buttonLabels = ButtonLabels;
  associationOptions = associationOptions;
  onboardVendorForm!: FormGroup;
  fileError: string | null = null;
  isSubmitted: boolean = false;
  fileSize: string | null = null;
  selectedFile: string | null = null;
  contacts!: FormArray;
  docuFilePath: string = '';

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private fileUploadService: FileUploadService,
    private toasterService: ToasterService,
  ) {}

  ngOnInit() {
    this.onboardVendorForm = new FormGroup({
      organisationName: new FormControl('', Validators.required),
      associationWithGovt: new FormControl('', Validators.required),
      referredBy: new FormControl('', Validators.required),
      gstNumber: new FormControl('', Validators.required),
      panNumber: new FormControl('', Validators.required),
      bankName: new FormControl('', Validators.required),
      accountNumber: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[0-9]*$/),
        Validators.min(1),
      ]),
      ifsc: new FormControl('', Validators.required),
      ASI_nameOfContact: new FormControl('', Validators.required),
      ASI_designation: new FormControl('', Validators.required),
      ASI_officePhone: new FormControl('', Validators.required),
      ASI_mobile: new FormControl('', [
        Validators.required,
        Validators.pattern(/^[0-9]*$/),
        Validators.min(1),
      ]),
      ASI_email: new FormControl('', [Validators.required, Validators.email]),
      ASI_location: new FormControl('', Validators.required),
      document: new FormControl('', Validators.required),
      contacts: this.fb.array([this.createContactGroup()]),
    });
    this.contacts = this.onboardVendorForm.get('contacts') as FormArray;
  }

  createContactGroup() {
    return this.fb.group({
      CI_name: ['', Validators.required],
      CI_designation: ['', Validators.required],
      CI_officePhone: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[0-9]*$/),
          Validators.min(1),
        ],
      ],
      CI_mobile: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[0-9]*$/),
          Validators.min(1),
        ],
      ],
      CI_email: ['', [Validators.required, Validators.email]],
      CI_location: ['', Validators.required],
    });
  }

  isFieldValid(fieldName: string): boolean {
    const control = this.onboardVendorForm.get(fieldName);
    if (this.isSubmitted || control?.touched) {
      return control?.valid ?? false;
    }
    return true;
  }

  getFieldError(fieldName: string): string {
    const control = this.onboardVendorForm.get(fieldName);
    return getFormControlError(control);
  }

  isFieldInvalidArray(array: FormArray, index: number, field: string): boolean {
    const control = array.at(index).get(field);
    return !!(
      control &&
      control.invalid &&
      (control.touched || this.isSubmitted)
    );
  }

  getFieldErrorTextArray(
    array: FormArray,
    index: number,
    field: string,
  ): string | undefined {
    const control = array.at(index).get(field);
    return getFormControlError(control);
  }

  navigateToOpsDashboard() {
    this.router.navigate(['ops-vendor-management']);
  }

  onFileSelected(event: Event): void {
    this.fileError = null;
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const fileType = file.type;
      const fileSizeInBytes = file.size;
      this.selectedFile = input.files[0].name;

      if (!validFileTypes.includes(fileType)) {
        this.fileError = ErrorMessages.InvalidFileType;
        this.selectedFile = null;
        this.onboardVendorForm
          .get('document')
          ?.setErrors({ invalidFileType: true });
        return;
      }

      const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
      if (fileSizeInBytes > MAX_FILE_SIZE) {
        this.fileSize = Math.floor(fileSizeInBytes / (1024 * 1024)) + ' MB';
        this.fileError = ErrorMessages.FileSizeExceeded;
        this.selectedFile = null;
        this.onboardVendorForm
          .get('document')
          ?.setErrors({ fileTooLarge: true });
        return;
      } else if (fileSizeInBytes > 1024) {
        this.fileSize = Math.floor(fileSizeInBytes / 1024) + ' KB';
      }

      this.selectedFile = file.name;
      this.onboardVendorForm
        .get('document')
        ?.setValidators(uploadedFileValidator(this.selectedFile));
      this.onboardVendorForm.get('document')?.updateValueAndValidity();
      this.fileUploadService.uploadFile(file, 'job_description').subscribe(
        (res) => {
          this.docuFilePath = res.filePath;
        },
        (error) => {
          this.toasterService.showError(error.error, 'Error');
        },
      );
    } else {
      this.onboardVendorForm
        .get('document')
        ?.setValidators(uploadedFileValidator(this.selectedFile));
      this.onboardVendorForm.get('document')?.updateValueAndValidity();
    }
  }

  onDeleteFile() {
    this.selectedFile = null;
    this.onboardVendorForm.get('document')?.setValue('');
    this.onboardVendorForm
      .get('document')
      ?.setValidators(uploadedFileValidator(this.selectedFile));
    this.onboardVendorForm.get('document')?.updateValueAndValidity();
  }

  onAddContact(event: Event) {
    event.preventDefault();
    this.contacts.push(this.createContactGroup());
  }

  removeContact(index: number) {
    if (this.contacts.length > 1) {
      this.contacts.removeAt(index);
    }
  }

  onSubmit() {
    this.isSubmitted = true;
  }

  onCancel() {
    this.router.navigate(['ops-vendor-management']);
  }
}
